import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Calendar, FileText, ExternalLink, Search, Clock, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LoadingPage } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { useLanguage } from "../contexts/LanguageContext";

const Exams = () => {
  const { token } = useContext(AuthContext);
  const { t } = useLanguage();
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    filterExams();
  }, [searchTerm, exams]);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/exams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExams(response.data);
      setFilteredExams(response.data);
    } catch (error) {
      toast.error("Failed to load exams");
    } finally {
      setLoading(false);
    }
  };

  const filterExams = () => {
    if (searchTerm) {
      const filtered = exams.filter(
        (exam) =>
          exam.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          exam.full_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredExams(filtered);
    } else {
      setFilteredExams(exams);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <LoadingPage message="Loading exams..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-testid="exams-container">
        {/* Compact Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t('exams.title')}
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                {t('exams.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder={t('exams.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 text-base border-none bg-muted/30"
                data-testid="exam-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        {filteredExams.length > 0 && (
          <div className="mb-6">
            <p className="text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredExams.length}</span> exam{filteredExams.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Exams List */}
        <div className="space-y-6">
          {filteredExams.map((exam) => (
            <Card key={exam.id} className="group border-none shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden" data-testid={`exam-card-${exam.id}`}>
              {/* Gradient Top Bar */}
              <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60"></div>
              
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <CardTitle className="text-2xl md:text-3xl group-hover:text-primary transition-colors">{exam.name}</CardTitle>
                      {exam.career_paths && exam.career_paths.length > 0 && (
                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 hidden md:inline-flex">
                          {exam.career_paths[0]}
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="text-base">{exam.full_name}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Date Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('exams.examDate')}</p>
                      <p className="font-bold text-foreground">{exam.exam_date}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-destructive/5 rounded-lg">
                    <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-destructive" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('exams.registrationDeadline')}</p>
                      <p className="font-bold text-foreground">{exam.registration_deadline}</p>
                    </div>
                  </div>
                </div>

                {/* Subjects */}
                {exam.subjects && exam.subjects.length > 0 && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" />
                      {t('common.subjects')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {exam.subjects.map((subject, idx) => (
                        <Badge key={idx} variant="secondary" className="text-sm">
                          {subject}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Accordion for Details */}
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="eligibility" className="border-none">
                    <AccordionTrigger className="hover:no-underline bg-muted/20 px-4 rounded-lg hover:bg-muted/40 transition-colors">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-success" />
                        {t('exams.eligibility')}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pt-4">
                      <ul className="space-y-3">
                        {exam.eligibility && exam.eligibility.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                            <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                            <span className="leading-relaxed">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  {exam.resources && exam.resources.length > 0 && (
                    <AccordionItem value="resources" className="border-none">
                      <AccordionTrigger className="hover:no-underline bg-muted/20 px-4 rounded-lg hover:bg-muted/40 transition-colors">
                        <span className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-primary" />
                          Preparation Resources
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pt-4">
                        <div className="space-y-2">
                          {exam.resources.map((resource, idx) => (
                            <Button key={idx} variant="outline" className="w-full justify-start group/btn hover:bg-primary/5 hover:border-primary/30 transition-colors" size="sm" asChild>
                              <a href={resource.url} target="_blank" rel="noopener noreferrer">
                                <BookOpen className="w-4 h-4 mr-2 text-primary" />
                                <span className="flex-1 text-left">{resource.title}</span>
                                <ExternalLink className="w-4 h-4 ml-auto opacity-50 group-hover/btn:opacity-100" />
                              </a>
                            </Button>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  <AccordionItem value="description" className="border-none">
                    <AccordionTrigger className="hover:no-underline bg-muted/20 px-4 rounded-lg hover:bg-muted/40 transition-colors">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        About This Exam
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pt-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">{exam.description}</p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredExams.length === 0 && (
          <EmptyState
            icon={FileText}
            title={t('exams.noResults') || "No exams found"}
            description="Try adjusting your search to find exams."
            data-testid="no-exams-message"
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Exams;